import { Link } from 'react-router-dom';
import { useLazyImage } from '../../../hooks/useLazyImage';
import { buildWebPSrcSet, toWebP } from '../../../lib/imageUtils';

interface BlogPostCardProps {
  post: any;
  priority?: boolean;
}

export default function BlogPostCard({ post, priority = false }: BlogPostCardProps) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });

  const imageUrl = post.image || post.heroImage || '';

  // Determine if post is "new this week" (published within last 7 days)
  const isNewThisWeek = (() => {
    const postDate = post.publishedAt || post.date;
    if (!postDate) return false;
    const parsed = new Date(postDate);
    if (isNaN(parsed.getTime())) return false;
    const diffMs = Date.now() - parsed.getTime();
    return diffMs >= 0 && diffMs <= 7 * 24 * 60 * 60 * 1000;
  })();

  const categoryColors: Record<string, string> = {
    'buying-guide': 'bg-[#E8B84B] text-[#1C2B1E]',
    maintenance: 'bg-[#1C2B1E] text-white',
    financing: 'bg-[#E8B84B] text-[#1C2B1E]',
    news: 'bg-[#1C2B1E] text-white',
    'Buyer Education': 'bg-[#E8B84B] text-[#1C2B1E]',
    'Dealership Comparison': 'bg-[#1C2B1E] text-white',
    'City Broker Guide': 'bg-[#E8B84B] text-[#1C2B1E]',
    'Buyer Guides': 'bg-[#1C2B1E] text-white',
    'Comparisons': 'bg-[#E8B84B] text-[#1C2B1E]',
    'Car Buying Tips': 'bg-[#E8B84B] text-[#1C2B1E]',
    'Recalls & Safety': 'bg-[#1C2B1E] text-white',
    'EV & Innovation': 'bg-[#E8B84B] text-[#1C2B1E]',
    'Market Trends': 'bg-[#1C2B1E] text-white',
    'Oregon Coast Living': 'bg-[#E8B84B] text-[#1C2B1E]',
  };

  const categoryLabel = post.category ? post.category.replace('-', ' ') : '';
  const imageTitle = `${categoryLabel}: ${post.title} — Rockhound Auto Blog`;
  const imageAlt = `${post.title} — ${categoryLabel} article featured image — Rockhound Auto Warrenton Oregon`;

  const parsedDate = post.date && !isNaN(Date.parse(post.date))
    ? new Date(post.date)
    : null;
  const displayDate = parsedDate
    ? parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : post.date || '';
  const isoDate = parsedDate ? parsedDate.toISOString().split('T')[0] : '';

  return (
    <article
      aria-label={post.title}
      className="bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col relative"
    >
      <Link to={`/blog/${post.slug}`} className="flex flex-col flex-1">
        {/* Image */}
        <div ref={priority ? undefined : ref} className="relative aspect-[16/9] overflow-hidden bg-gray-100 flex-shrink-0">
          {(priority || isVisible) && imageUrl ? (
            <picture>
              <source
                type="image/webp"
                srcSet={buildWebPSrcSet(imageUrl)}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 800px"
              />
              <img
                src={toWebP(imageUrl, 800)}
                alt={imageAlt}
                title={imageTitle}
                loading={priority ? 'eager' : 'lazy'}
                decoding={priority ? 'sync' : 'async'}
                fetchPriority={priority ? 'high' : 'low'}
                width={800}
                height={450}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </picture>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] animate-pulse" aria-hidden="true" />
          )}

          {/* Category Badge */}
          <div
            className={`absolute top-3 left-3 px-3 py-1.5 rounded-full font-bold text-xs uppercase ${
              categoryColors[post.category] || 'bg-[#1C2B1E] text-white'
            }`}
            aria-label={`Category: ${categoryLabel}`}
          >
            {categoryLabel}
          </div>

          {/* 2 Bald Guys Pick Badge */}
          {post.featured && (
            <div
              className="absolute top-14 left-3 px-3 py-1.5 rounded-full font-bold text-xs bg-gradient-to-r from-[#E8B84B] to-[#d4a43d] text-[#1C2B1E] shadow-lg border-2 border-white/50 backdrop-blur-sm flex items-center gap-1.5"
              aria-label="2 Bald Guys Pick"
            >
              <span className="text-sm" aria-hidden="true">🧑‍🦲🧑‍🦲</span>
              <span className="uppercase tracking-wide">Bald Guys Pick</span>
            </div>
          )}

          {/* New This Week Badge */}
          {isNewThisWeek && (
            <div
              className="absolute top-3 right-3 px-2.5 py-1 rounded-full font-bold text-xs uppercase tracking-wide bg-emerald-500 text-white shadow-md flex items-center gap-1.5 animate-pulse"
              aria-label="New this week"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white inline-block flex-shrink-0" aria-hidden="true" />
              New This Week
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1">
          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-[#4A5568] mb-2">
            <div className="flex items-center gap-1">
              <i className="ri-calendar-line text-[#E8B84B]" aria-hidden="true"></i>
              {isoDate ? (
                <time dateTime={isoDate}>{displayDate}</time>
              ) : (
                <span>{displayDate}</span>
              )}
            </div>
            {post.readTime && (
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-[#E8B84B]" aria-hidden="true"></i>
                <span>{post.readTime}</span>
              </div>
            )}
          </div>

          {/* Title */}
          <h3 className="font-['Oswald'] text-lg font-bold text-[#1C2B1E] mb-2 group-hover:text-[#E8B84B] transition-colors line-clamp-2 flex-1">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-[#374151] text-sm mb-4 line-clamp-2">{post.excerpt}</p>

          {/* Author */}
          <div className="flex items-center gap-3 pt-3 border-t border-[#E8E2D6]">
            <div
              className="w-8 h-8 bg-[#E8B84B] rounded-full flex items-center justify-center text-[#1C2B1E] font-bold text-sm flex-shrink-0"
              aria-hidden="true"
            >
              {post.author ? post.author.charAt(0) : 'R'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1C2B1E] text-sm truncate">{post.author}</p>
            </div>
            <span className="text-[#92650a] font-medium text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap" aria-hidden="true">
              Read
              <i className="ri-arrow-right-line"></i>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}